/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio

/**
 * Month-by-month amortization for a [DebtScenario]. Pure (no I/O).
 *
 * Treats all debt as fixed-rate amortizing for Phase 1 — the math is
 * standard `interest = balance × rate/12`, `principal = payment − interest`.
 * Variable-rate projections and mortgage term/amortization split land in
 * Phase 2.
 */
object DebtPayoffProjector {

    /** Hard cap so credit-card scenarios with negative amortization don't
     *  loop forever. Equivalent to ~40 years. */
    private const val MAX_MONTHS = 480

    fun project(scenario: DebtScenario): DebtProjection {
        // Apply lump sum upfront. If it pays off the debt outright, return
        // a single-point projection with zero interest.
        val startingBalance = (scenario.initialBalance - scenario.lumpSum).coerceAtLeast(0.0)
        if (startingBalance <= 0.0) {
            return DebtProjection(
                scenario = scenario,
                points = listOf(DebtPoint(month = 0, balance = 0.0)),
                monthsToPayoff = 0,
                totalInterestPaid = 0.0,
                totalCost = scenario.lumpSum.coerceAtLeast(0.0),
            )
        }

        val freq = scenario.paymentFrequency
        val periodsPerYear = freq.periodsPerYear()
        val periodRate = scenario.annualRate / periodsPerYear
        val periodPayment = freq.periodPayment(scenario.monthlyPayment).coerceAtLeast(0.0)

        // Negative amortization: first-period payment can't cover interest.
        val firstPeriodInterest = startingBalance * periodRate
        val negativeAmortization = periodPayment <= firstPeriodInterest && periodRate > 0.0

        val points = mutableListOf<DebtPoint>()
        points += DebtPoint(month = 0, balance = startingBalance)

        var balance = startingBalance
        var totalInterestPaid = 0.0
        var lastEmittedMonth = 0
        var periodCount = 0
        val maxPeriods = MAX_MONTHS * periodsPerYear / 12

        while (balance > 0.0 && periodCount < maxPeriods) {
            val interest = balance * periodRate
            val principal = (periodPayment - interest)
            balance = (balance - principal).coerceAtLeast(0.0)
            totalInterestPaid += interest.coerceAtLeast(0.0)
            periodCount++

            // Emit a snapshot whenever a new calendar month boundary passes.
            // (periodCount * 12 / periodsPerYear) is the integer month index at
            // the end of this period; we emit when it advances past the last
            // emitted month. Works for all three frequencies.
            val currentMonth = periodCount * 12 / periodsPerYear
            if (currentMonth > lastEmittedMonth) {
                points += DebtPoint(month = currentMonth, balance = balance)
                lastEmittedMonth = currentMonth
            }
            if (balance <= 0.0) {
                // Guarantee the terminal point lands at balance = 0 in the payoff
                // month. The payoff month uses the rounded-up convention so the
                // chart agrees with monthsToPayoff. If that month already has a
                // snapshot (an earlier period in the same month emitted while
                // balance was still positive), replace it. Otherwise append.
                val payoffMonth = (periodCount * 12 + periodsPerYear - 1) / periodsPerYear
                if (points.last().month == payoffMonth) {
                    points[points.size - 1] = DebtPoint(month = payoffMonth, balance = 0.0)
                } else {
                    points += DebtPoint(month = payoffMonth, balance = 0.0)
                }
                break
            }
        }

        val monthsToPayoff = if (balance <= 0.0) {
            // Round UP — if the final period lands mid-month, the user still
            // pays interest for that month.
            (periodCount * 12 + periodsPerYear - 1) / periodsPerYear
        } else {
            MAX_MONTHS
        }

        val totalCost = scenario.lumpSum.coerceAtLeast(0.0) + startingBalance + totalInterestPaid

        return DebtProjection(
            scenario = scenario,
            points = points,
            monthsToPayoff = monthsToPayoff,
            totalInterestPaid = totalInterestPaid,
            totalCost = totalCost,
            negativeAmortization = negativeAmortization,
        )
    }

    /** Standard amortization formula — monthly payment for [balance] paid
     *  off over [months] at [annualRate]. Used to derive a sensible
     *  "current plan" payment when the user hasn't entered one explicitly. */
    fun amortizingPayment(balance: Double, annualRate: Double, months: Int): Double {
        if (months <= 0 || balance <= 0.0) return 0.0
        val r = annualRate / 12.0
        if (r <= 0.0) return balance / months
        val n = months.toDouble()
        val factor = kotlin.math.exp(n * kotlin.math.ln(1.0 + r))
        return balance * r * factor / (factor - 1.0)
    }
}
